import { ArrowBack, Refresh } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { isRouteErrorResponse, useNavigate, useParams, useRouteError } from 'react-router-dom';

/**
 * Error boundary component for user-detail routes that presents contextual messages and recovery actions.
 *
 * Renders a user-facing error panel for errors that occur while loading user detail pages (accounts,
 * transactions, etc.), mapping route (HTTP) errors and JS errors to friendly messages and optional
 * details. Provides navigation and recovery actions: "Back to Users", "User Overview" (when an `id`
 * param is present), and "Try Again" (reloads the page; hidden for certain HTTP statuses). In
 * development mode the component also exposes the original error message/stack for debugging.
 *
 * @returns A React element containing the error UI and action buttons.
 */
export function UserDetailError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { id } = useParams();

  // Determine error type and message
  let errorMessage = 'An unexpected error occurred';
  let errorDetails: string | undefined;
  let showRetry = true;

  if (isRouteErrorResponse(error)) {
    // Handle HTTP errors from loaders or actions
    switch (error.status) {
      case 404:
        errorMessage = 'User not found';
        errorDetails = `User with ID ${id} could not be found. They may have been deleted or the ID is incorrect.`;
        showRetry = false;
        break;
      case 401:
        errorMessage = 'Access denied';
        errorDetails = "You do not have permission to view this user's details.";
        showRetry = false;
        break;
      case 403:
        errorMessage = 'Forbidden';
        errorDetails =
          'Your account does not have the required permissions to access this resource.';
        showRetry = false;
        break;
      case 500:
        errorMessage = 'Server error';
        errorDetails = 'There was a problem on our end. Please try again in a moment.';
        break;
      default:
        errorMessage = `Error ${error.status}`;
        errorDetails = error.statusText || 'An unexpected server error occurred.';
    }
  } else if (error instanceof Error) {
    // Handle JavaScript errors
    if (error.message.includes('Failed to fetch')) {
      errorMessage = 'Network error';
      errorDetails =
        'Unable to connect to the server. Please check your internet connection and try again.';
    } else if (error.message.includes('updater is not a function')) {
      errorMessage = 'Table configuration error';
      errorDetails =
        'There was an issue with the table display. Please refresh the page to continue.';
    } else {
      errorMessage = 'Application error';
      errorDetails = process.env.NODE_ENV === 'development' ? error.message : undefined;
    }
  } else {
    errorMessage = 'Application error';
    errorDetails = process.env.NODE_ENV === 'development' ? String(error) : undefined;
  }

  /**
   * Navigate back to the users list
   */
  const handleBackToUsers = () => {
    navigate('/dashboard/admin/users');
  };

  /**
   * Refresh the current page to retry loading
   */
  const handleRetry = () => {
    window.location.reload();
  };

  /**
   * Navigate back to user overview if we're on a specific tab
   */
  const handleBackToUserOverview = () => {
    if (id) {
      navigate(`/dashboard/admin/users/${id}`);
    } else {
      handleBackToUsers();
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="h6" component="div" sx={{ mb: 1 }}>
          {errorMessage}
        </Typography>
        {errorDetails && (
          <Typography variant="body2" color="text.secondary">
            {errorDetails}
          </Typography>
        )}
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackToUsers}>
          Back to Users
        </Button>

        {id && (
          <Button variant="outlined" onClick={handleBackToUserOverview}>
            User Overview
          </Button>
        )}

        {showRetry && (
          <Button variant="contained" startIcon={<Refresh />} onClick={handleRetry} color="primary">
            Try Again
          </Button>
        )}
      </Stack>

      {process.env.NODE_ENV === 'development' && error instanceof Error && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Development Error Details:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
              maxHeight: 200,
            }}
          >
            {error.stack || error.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
